import { DatePipe } from "@angular/common";
import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { shortDateFormat } from "src/app/shared/dateTimeHelpers";
import { DataListTagGroup } from "src/app/shared/models/data-list";
import { KeyValuePair } from "src/app/shared/models/key-value-pair";
import { AdministrativeUnit } from "src/app/shared/models/organizations/administrative-unit";
import { withSanitizedName } from "src/app/shared/utilities/file-name-sanitizer";
import { TagPickerComponent } from "../../tag-picker/tag-picker.component";
import { DialogData } from "./upload-modal.models";

@Component({
    templateUrl: "./upload-modal.component.html",
    styleUrls: ["./upload-modal.component.scss"],
    standalone: false
})
export class UploadModalComponent implements OnInit {
  @ViewChild("fileInput") fileInput;
  @ViewChild(TagPickerComponent) tagPicker: TagPickerComponent;
  @Output() uploadFiles = new EventEmitter();

  formArray: UntypedFormArray;
  dateFormat: string = shortDateFormat;
  pipe = new DatePipe("en-US");
  documentationTypeOptions: KeyValuePair[];
  cardHeader: string;
  fileTitle: string;
  maxFileSize: number;
  maxFileSizeLabel: string;
  availableTags: DataListTagGroup[] = [];
  preselectedTagIds: string[] = [];
  allowedFileTypes: string =
    "TXT, CSV, PDF, DOC, DOCX, ODT, PPTX, PPT, RTF, XLS, XLSX, XLSM, PNG, JPEG, JPG, GIF, BMP, ZIP";

  listOfFiles: File[];
  errors: string[];
  step: number = 1;

  selectedTagsCount: number;

  selectedSchoolYear: string = "";
  selectedDocumentType: string = "";
  selectedAdministrativeUnit: string = "";
  selectedReleaseDate: Date;
  selectedAdministrativeUnitSurveyId: string = "";
  selectedStudentId: string = "";
  selectedIndicatorType: number = -1;

  isSchoolYearVisible: boolean = true;
  isAdministrativeUnitVisible: boolean = true;
  isReleaseDateVisible: boolean = true;

  preselectedFiles: File[];

  get formArrayControls() {
    return this.formArray.controls as UntypedFormGroup[];
  }

  constructor(
    private dialogRef: MatDialogRef<UploadModalComponent>,
    private fb: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    //this.documentationTypeOptions = data.documentationTypes;
    this.fileTitle = data.fileName;
    this.cardHeader = data.title ?? "Upload Documentation";
    this.allowedFileTypes = data.allowedFileTypes ?? this.allowedFileTypes;
    this.maxFileSize = data.maxFileSize ?? 209715200;
    this.maxFileSizeLabel = data.maxFileSizeLabel ?? "200 MB";
    this.selectedSchoolYear = data.preselectedSchoolYear;
    this.selectedDocumentType = "2"; //always document for now -- data.preselectedDocumentType;
    this.selectedAdministrativeUnit = data.preselectedAdministrativeUnit;
    this.preselectedFiles = data.preselectedFiles;
    this.selectedAdministrativeUnitSurveyId = data.preselectedAdministrativeUnitSurveyId;
    this.selectedStudentId = data.preselectedStudentId;
    this.selectedIndicatorType = data.preselectedIndicatorType;
    this.isSchoolYearVisible = data.isSchoolYearVisible;
    this.isAdministrativeUnitVisible = data.isAdministrativeUnitVisible;
    this.isReleaseDateVisible = data.isReleaseDateVisible || true;
  }

  ngOnInit(): void {
    this.errors = [];
    this.formArray = this.fb.array([]);
    this.selectedTagsCount = this.data.preselectedTagIds.length;

    if (!this.data.isStateUser) {
      this.selectedReleaseDate = new Date();
    } else {
      if (
        this.data.availableAdministrativeUnits.findIndex((i) => i.name === "All AUs") < 0
      ) {
        const fakeAllAuAdministrativeUnit = {
          id: "00000000-0000-0000-0000-000000000000",
          name: "All AUs",
        } as AdministrativeUnit;
        this.data.availableAdministrativeUnits.unshift(fakeAllAuAdministrativeUnit);
      }
    }

    if (this.preselectedFiles) {
      this.errors = [];
      this.listOfFiles = [];
      this.selectFiles(this.preselectedFiles);
    }
  }

  openNativeFileSelector() {
    this.errors = [];
    this.listOfFiles = [];

    if (this.fileInput) this.fileInput.nativeElement.click();
  }

  selectFiles(files) {
    for (let key in files) {
      if (!isNaN(parseInt(key))) {
        const file = files[key];
        if (this.isValidFile(file)) this.listOfFiles.push(withSanitizedName(file));
      }
    }
    if (this.errors.length === 0) this.addFilesToList();
  }

  onFileSelected() {
    const files: { [key: string]: File } = this.fileInput.nativeElement.files;
    return this.selectFiles(files);
  }

  private isValidFile(file: File): boolean {
    if (!(file && file.name)) {
      this.errors.push(`Invalid file or files`);
      return false;
    }

    const ext = file.name.split(".").slice(-1)[0];
    if (!ext) {
      this.errors.push(`Cannot determine file type`);
      return false;
    }

    if (!this.allowedFileTypes.toLowerCase().includes(ext.toLowerCase())) {
      this.errors.push(`File type '${ext}' is not supported`);
      return false;
    }

    if (file.size === 0) {
      this.errors.push(`File cannot be empty: ${file.name}`);
      return false;
    }

    if (file.size > this.maxFileSize) {
      this.errors.push(
        `File cannot be larger than ${this.maxFileSizeLabel}: ${file.name}`
      );
      return false;
    }

    return true;
  }

  onRemoveAt(index: number) {
    this.formArray.removeAt(index);
  }

  addFilesToList() {
    const formArray = this.formArray;
    formArray.clear();

    for (const file of this.listOfFiles) {
      formArray.push(
        this.fb.group({
          title: [this.fileTitle, Validators.required],
          type: [""],
          file: [file],
        })
      );
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  confirm() {
    if (this.formArray.invalid) {
      this.formArray.markAllAsTouched();
      return;
    }

    var results = {
      formArray: this.formArray.value,
      selectedTagIds: this.tagPicker.getSelectedTagIds().join(),
      selectedDocumentType: this.selectedDocumentType,
      selectedSchoolYear: this.selectedSchoolYear,
      selectedAdministrativeUnit: this.selectedAdministrativeUnit,
      selectedAdministrativeUnitSurveyId: this.selectedAdministrativeUnitSurveyId,
      selectedStudentId: this.selectedStudentId,
      selectedIndicatorType: this.selectedIndicatorType,
      selectedReleaseDate: this.selectedReleaseDate.toISOString(),
    };

    console.log("confirm", results);

    this.uploadFiles.emit(results);
  }

  hasRequiredItems() {
    return (
      this.selectedTagsCount > 0 &&
      this.selectedDocumentType &&
      this.selectedSchoolYear &&
      this.selectedAdministrativeUnit &&
      this.selectedReleaseDate
    );
  }

  selectedTagCountChanged(count: number) {
    this.selectedTagsCount = count;
  }

  next() {
    this.step = 2;
  }
}
